'use client';

/**
 * HeaderTabs — 타이틀 영역 + 기본 Tab + 선택된 패널
 *
 * 탭 상태·접근성은 components/ui/Tab이 담당한다.
 * 탭 버튼 색·상태는 루트 Tab type="button"(채움 기본) + outline(tabLine) 을 쓴다.
 * 이 컴포넌트는 상품/섹션 화면에서 쓰는 타이틀, 설명, CTA, row/column 배치와
 * 탭 버튼 size 스타일을 맡는다.
 */

import clsx from 'clsx';
import s from './HeaderTabs.module.scss';
import type { ReactNode } from 'react';
import { Title, TitleGroup } from '@publishing/components/ui/Title';
import Tab, {
  TabButton,
  TabList,
  TabPanel,
  useTab,
  type TabItem,
  type TabWidth,
} from '@publishing/components/ui/Tab';
import { Button, type ButtonConfig } from '@publishing/components/ui/Button';
import { resolveResponsiveValue, type ResponsiveValue } from '@publishing/helpers/responsive';

export type { TabItem };
export type HeaderTabsWidth = Extract<TabWidth, number>;

type HeaderTabsBaseDataProps = {
  /** 탭 버튼 폭 112 | 170. `[pc, mo]` 분기 가능. 기본 170 — 112: 한 줄 3개 / 170: 한 줄 2개 (가로형 PC·모바일) */
  tabWidth?: ResponsiveValue<HeaderTabsWidth>;
  /** 탭 버튼 테두리 (Figma "Line On/Off"). 생략 시 off — CarouselTabsSection이 theme 기준으로 정해 넘긴다 */
  tabLine?: boolean;
  subTitle?: string;
  title: ReactNode;
  desc?: ReactNode;
};

type HeaderTabsColumnDataProps = HeaderTabsBaseDataProps & {
  direction?: 'column';
  button?: ButtonConfig;
  subText?: ReactNode | ReactNode[];
};

type HeaderTabsRowDataProps = HeaderTabsBaseDataProps & {
  direction: 'row';
  button?: never;
  subText?: never;
};

export type HeaderTabsDataProps =
  | HeaderTabsColumnDataProps
  | HeaderTabsRowDataProps;

interface HeaderTabsContentsProps {
  tabs: TabItem[];
  renderPanel?: (tab: TabItem, activeIndex: number) => ReactNode;
  /** true 면 모든 탭 패널을 미리 렌더하고 비활성은 hidden 처리 — 탭 전환 깜빡임 방지 */
  renderAllPanels?: boolean;
  index?: number;
  className?: string;
}

export type HeaderTabsProps = HeaderTabsContentsProps & HeaderTabsDataProps;

interface HeaderTabsHeaderProps {
  subTitle?: string;
  title: ReactNode;
  desc?: ReactNode;
}

export default function HeaderTabs({
  direction = 'column',
  tabWidth = 170,
  tabLine,
  tabs,
  renderPanel,
  renderAllPanels,
  index = 0,
  className,
  subTitle,
  title,
  desc,
  subText,
  button,
}: HeaderTabsProps) {
  const isRow = direction === 'row';
  const { text: buttonText, ...buttonProps } = button ?? {};
  const [pcWidth, moWidth] = resolveResponsiveValue(tabWidth);

  return (
    <Tab tabs={tabs} index={index} type="button" outline={tabLine}>
      <div
        className={clsx(s.product_tabs, isRow && `${s.product_tabs_row} tabs_row`, className)}
      >
        <div className={s.product_tabs_control}>
          <TabHeader subTitle={subTitle} title={title} desc={desc} />
          <TabList
            className={clsx(s.tab_list, isRow && s.tab_list_grid, s[`tab_list_${pcWidth}`], moWidth && s[`tab_list_mobile_${moWidth}`], s.product_tabs_list)}
          >
            {tabs.map((tab, tabIndex) => (
              <li key={tabIndex} role="presentation">
                <TabButton index={tabIndex} className={s.tab_button}>
                  {tab.text}
                </TabButton>
              </li>
            ))}
          </TabList>
        </div>

        <TabPanel
          className={s.product_tabs_panel}
          renderPanel={renderPanel}
          renderAllPanels={renderAllPanels}
        />

        {!isRow && (
          <>
            <TabSubText subText={subText} />
            {button && (
              <Button
                size="large"
                className={`${s.product_tabs_button} ${s.product_tabs_button_bottom}`}
                {...buttonProps}
              >
                {buttonText}
              </Button>
            )}
          </>
        )}
      </div>
    </Tab>
  );
}

function TabHeader({ subTitle, title, desc }: HeaderTabsHeaderProps) {
  return (
    <TitleGroup align="center" className={s.product_tabs_title_group}>
      {subTitle && <p className={s.product_tabs_subtitle}>{subTitle}</p>}
      <Title className={s.product_tabs_title}>{title}</Title>
      {desc && <p className={s.product_tabs_desc}>{desc}</p>}
    </TitleGroup>
  );
}

/** subText가 배열이면 활성 탭 index의 문구만 노출 */
function TabSubText({ subText }: { subText?: ReactNode | ReactNode[] }) {
  const { activeIndex } = useTab();
  const text = Array.isArray(subText) ? subText[activeIndex] : subText;

  return text ? <p className={s.product_tabs_text}>{text}</p> : null;
}
